import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, Search, Hammer, TrendingUp, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import WaterCanvas from "@/components/WaterCanvas";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  }),
};

const statusClass = { Live: "status-live", MVP: "status-mvp", Idea: "status-idea" };

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    axios.get(`${API}/projects`).then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  return (
    <main className="overflow-hidden">
      {/* ===== HERO ===== */}
      <section className="hero-bg min-h-screen flex items-center relative" data-testid="hero-section">
        <WaterCanvas />
        <div className="orb orb-1"></div>
        <div className="orb orb-2"></div>
        <div className="orb orb-3"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 md:pt-40 md:pb-32 w-full text-center">
          <motion.div initial="hidden" animate="visible" className="max-w-3xl mx-auto">
            <motion.p
              variants={fadeUp}
              custom={0}
              className="font-mono text-xs md:text-sm tracking-[0.25em] uppercase text-slate-400 mb-6"
            >
              Venture Studio &mdash; Blockchain &middot; AI &middot; Finance
            </motion.p>

            <motion.h1
              variants={fadeUp}
              custom={1}
              className="font-serif text-6xl sm:text-7xl md:text-[8rem] font-bold text-slate-900 tracking-tight leading-[0.85] mb-8"
            >
              Upgrade<br />
              <span className="gradient-text">Finance.</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              custom={2}
              className="font-sans text-base md:text-lg text-slate-500 leading-relaxed max-w-xl mx-auto mb-10"
            >
              A venture studio building and funding the infrastructure of programmable finance.
            </motion.p>

            <motion.div variants={fadeUp} custom={3} className="flex flex-wrap justify-center gap-4">
              <Button
                size="lg"
                onClick={() => navigate("/portfolio")}
                className="bg-slate-900 text-white hover:bg-gradient-to-r hover:from-blue-600 hover:to-cyan-400 font-sans text-sm px-8 h-12 btn-shimmer transition-all duration-300"
                data-testid="hero-explore-btn"
              >
                Explore Portfolio
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate(user ? "/dashboard" : "/auth?tab=register")}
                className="border-slate-200 text-slate-700 hover:border-blue-400 hover:text-blue-600 font-sans text-sm px-8 h-12 transition-all duration-300"
                data-testid="hero-join-btn"
              >
                Join Us
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ===== VISION ===== */}
      <section className="py-24 md:py-32 bg-white" data-testid="vision-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="section-gradient-line mb-16"></div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-12 gap-12"
          >
            <motion.div variants={fadeUp} custom={0} className="md:col-span-4">
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-slate-400 mb-4">Our Vision</p>
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">
                Finance is being <span className="gradient-text">rewritten.</span>
              </h2>
            </motion.div>
            <motion.div variants={fadeUp} custom={1} className="md:col-span-8 space-y-6">
              <p className="font-sans text-base md:text-lg text-slate-600 leading-relaxed">
                Not by institutions, but by builders who think differently and ship relentlessly.
              </p>
              <p className="font-sans text-base md:text-lg text-slate-600 leading-relaxed">
                SupraLife.Ventures is a venture studio founded by seasoned entrepreneurs at the intersection of blockchain, AI, and finance. We initiate, build, and fund ventures that form the infrastructure of a more open, programmable world.
              </p>
              <p className="font-sans text-base md:text-lg text-slate-600 leading-relaxed">
                Every project in our portfolio is a building block &mdash; designed to work together, designed to last.
              </p>
              <p className="font-sans text-base md:text-lg text-slate-500 leading-relaxed italic">
                We're looking for builders who want to create and investors who see what's coming. If you're here, you might be one of them.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}>
            <motion.p variants={fadeUp} custom={0} className="font-mono text-xs tracking-[0.25em] uppercase text-slate-400 mb-4 text-center">
              Three Paths
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-serif text-4xl md:text-5xl font-bold text-slate-900 tracking-tight text-center mb-16">
              How it works
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {[
              { icon: Search, title: "Explore", desc: "Browse our portfolio. See what we're building and where each venture stands.", color: "from-blue-600 to-blue-400" },
              { icon: Hammer, title: "Build", desc: "Join a project as a builder. Bring your skills, ideas, and energy to ventures that matter.", color: "from-cyan-500 to-cyan-400" },
              { icon: TrendingUp, title: "Invest", desc: "Back what's coming. Get access to opportunities at the earliest stage.", color: "from-blue-600 to-cyan-400" },
            ].map((card, i) => (
              <motion.div
                key={card.title}
                variants={fadeUp}
                custom={i + 2}
                className="bg-white border border-slate-200 rounded-xl p-8 md:p-10 hover:shadow-lg hover:border-blue-300 transition-all duration-300"
              >
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${card.color} flex items-center justify-center mb-6`}>
                  <card.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-serif text-2xl font-bold text-slate-900 mb-3">{card.title}</h3>
                <p className="font-sans text-slate-600 leading-relaxed">{card.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== PORTFOLIO PREVIEW ===== */}
      <section className="py-24 md:py-32 bg-white" data-testid="portfolio-preview-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}>
            <motion.p variants={fadeUp} custom={0} className="font-mono text-xs tracking-[0.25em] uppercase text-slate-400 mb-4">
              Portfolio
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-serif text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-4">
              Our Ventures
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="font-sans text-base md:text-lg text-slate-500 leading-relaxed max-w-2xl mb-12">
              Our ventures are the building blocks of a more open financial world. Each one solves a real problem. Together, they form a system.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {projects.slice(0, 6).map((project, i) => (
              <motion.div key={project.project_id} variants={fadeUp} custom={i + 3}>
                <Link to={`/project/${project.project_id}`} data-testid={`project-card-${project.name.toLowerCase().replace(/\s/g, '-')}`}>
                  <div
                    className="project-card bg-white border border-slate-200 rounded-xl p-6 cursor-pointer h-full"
                    style={{ borderLeftWidth: "3px", borderLeftColor: project.brand_color }}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-400">
                        {project.category}
                      </span>
                      <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${statusClass[project.status] || "status-idea"}`}>
                        {project.status}
                      </span>
                    </div>
                    <h3 className="font-serif text-xl font-bold text-slate-900 mb-2">{project.name}</h3>
                    <p className="font-sans text-sm text-slate-500 leading-relaxed">{project.tagline}</p>
                    {project.url && (
                      <div className="mt-4 flex items-center gap-1 text-xs text-slate-400">
                        <ExternalLink className="w-3 h-3" />
                        <span className="font-mono">{project.url.replace("https://", "")}</span>
                      </div>
                    )}
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>

          {projects.length > 6 && (
            <div className="mt-10 text-center">
              <Link to="/portfolio">
                <Button variant="outline" className="border-slate-200 text-slate-600 hover:border-blue-400 hover:text-blue-600 font-sans" data-testid="view-all-projects-btn">
                  View All Projects <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="py-24 md:py-32 bg-slate-50 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-cyan-400/20 rounded-full filter blur-[100px]"></div>
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-gradient-to-br from-cyan-400/20 to-blue-400/20 rounded-full filter blur-[80px]"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}>
            <motion.h2 variants={fadeUp} custom={0} className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight mb-6">
              This is just the <span className="gradient-text">beginning.</span>
            </motion.h2>
            <motion.p variants={fadeUp} custom={1} className="font-sans text-base md:text-lg text-slate-500 leading-relaxed max-w-xl mx-auto mb-10">
              We're building with a small circle of exceptional builders and forward-thinking investors. If you see what we see, join us.
            </motion.p>
            <motion.div variants={fadeUp} custom={2}>
              <Button
                size="lg"
                onClick={() => navigate(user ? "/dashboard" : "/auth?tab=register")}
                className="bg-slate-900 text-white hover:bg-gradient-to-r hover:from-blue-600 hover:to-cyan-400 font-sans text-sm px-10 h-12 btn-shimmer transition-all duration-300"
                data-testid="cta-create-account-btn"
              >
                {user ? "Go to Dashboard" : "Create an Account"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
